import { useMutation } from '@tanstack/react-query'
import requestDemo from 'businessTraveller/api/requestPostRequestDemo'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Select from 'components/Common/Form/Input/Select'
import TextInput from 'components/Common/Form/Input/TextInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import React, { FormEventHandler, useCallback, useState } from 'react'
import styled from 'styled-components'

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(20)};

  /* Ensure all grid items align at the end (bottom) */
  align-items: end;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "fullName mobileNumber"
                        "position workEmail"
                        "companyName businessLocation"
                        "businessLocationOther businessLocationOther"
                        "roleProgram roleProgram"
                        "travelSize travelSolution"
                        "message message";

    .grid-fullName {
      grid-area: fullName;
    }
    .grid-mobileNumber {
      grid-area: mobileNumber;
    }
    .grid-position {
      grid-area: position;
    }
    .grid-workEmail {
      grid-area: workEmail;
    }
    .grid-companyName {
      grid-area: companyName;
    }
    .grid-businessLocation {
      grid-area: businessLocation;
    }
    .grid-businessLocationOther {
      grid-area: businessLocationOther;
    }
    .grid-roleProgram {
      grid-area: roleProgram;
    }
    .grid-travelSize {
      grid-area: travelSize;
    }
    .grid-travelSolution {
      grid-area: travelSolution;
    }
    .grid-message {
      grid-area: message;
    }
  }
`

export interface RequestDemoData {
  fullName: string;
  mobileNumber: string;
  positionInCompany: string;
  workEmail: string;
  companyName: string;
  businessLocation: string;
  roleInTravelProgram: string;
  companyTravelSize: string;
  currentTravelSolution: string;
  message?: string;
}

export interface FormData {
  fullName: string;
  mobileNumber: { phone: string, prefix: string };
  jobTitle: string;
  workEmail: string;
  companyName: string;
  businessLocation: string;
  businessLocationOther?: string;
  roleInTravelProgram: string;
  companyTravelSize: string;
  currentTravelSolution: string;
  message?: string;
}

function mapFormData(formData: FormData): RequestDemoData {
  return {
    fullName: formData.fullName,
    mobileNumber: `${formData.mobileNumber.prefix} ${formData.mobileNumber.phone}`,
    positionInCompany: formData.jobTitle,
    workEmail: formData.workEmail,
    companyName: formData.companyName,
    businessLocation: formData.businessLocation === 'Other'
      ? formData.businessLocationOther || 'Other'
      : formData.businessLocation,
    roleInTravelProgram: formData.roleInTravelProgram,
    companyTravelSize: formData.companyTravelSize,
    currentTravelSolution: formData.currentTravelSolution,
    message: formData.message || undefined,
  }
}

interface RequestDemoFormProps {
  onSuccess?: () => void;
  showDescription?: boolean;
}

export default function RequestDemoForm({ onSuccess, showDescription = true }: RequestDemoFormProps) {
  const [isError, setIsError] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [finished, setFinished] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [businessLocation, setBusinessLocation] = useState<string>('')

  const emailSendMutation = useMutation({
    mutationFn: async(userData: RequestDemoData) => {
      return requestDemo(userData)
    },
    onSuccess: async() => {
      setFinished(true)
      onSuccess?.()
    },
    onError: async(error: App.ApiCallFailure) => {
      setErrorMsg(error.message)
      setIsError(true)
      setIsSubmitted(false)
    },
  })

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsSubmitted(true)
    const formData = formToObject<FormData>(e.currentTarget)
    const mappedFormData = mapFormData(formData)
    emailSendMutation.mutate(mappedFormData)
  }, [emailSendMutation])

  if (finished) {
    return (
      <Group direction="vertical" gap={20}>
        <BodyText variant="medium">
          Your request has been successfully submitted. Our Business Travel Specialist will reach out to you shortly.
        </BodyText>
      </Group>
    )
  }

  return (
    <Group direction="vertical" gap={20}>
      {isError && (
        <MessageBanner
          kind="critical"
          description={`Something went wrong while setting up your booking. Error message: ${errorMsg}`}
        />
      )}

      {showDescription && (
        <BodyText variant="medium">
          Our Business Travel Specialist will reach out to you to arrange a tailored meeting
        </BodyText>
      )}

      <form onSubmit={handleSubmit}>
        <InputGrid>
          <TextInput
            className="grid-fullName"
            invalidErrorMessage="Invalid characters. Please use only letters and spaces"
            label="Full Name"
            maxLength={100}
            minLength={2}
            name="fullName"
            pattern="^[A-Za-z ]+$"
            placeholder="John Smith"
            required
            requiredErrorMessage="Full name is required"
          />

          <PhoneInput
            className="grid-mobileNumber"
            defaultPhonePrefix="+61"
            label="Mobile Number"
            name="mobileNumber"
            placeholder="0400 000 000"
            required
            requiredErrorMessage="Mobile number is required"
          />

          <TextInput
            className="grid-position"
            label="Job Title"
            maxLength={100}
            minLength={2}
            name="jobTitle"
            placeholder="Travel Manager"
            required
            requiredErrorMessage="Job title is required"
          />

          <TextInput
            className="grid-workEmail"
            invalidErrorMessage="Invalid email address"
            label="Work Email Address"
            maxLength={100}
            name="workEmail"
            placeholder="Enter your business email"
            required
            requiredErrorMessage="Work email address is required"
            type="email"
          />

          <TextInput
            className="grid-companyName"
            label="Company Name"
            maxLength={200}
            minLength={2}
            name="companyName"
            placeholder="Your company name"
            required
            requiredErrorMessage="Company name is required"
          />

          <Select
            className="grid-businessLocation"
            label="Business Location"
            name="businessLocation"
            placeholder="Select your state"
            required
            requiredErrorMessage="Business location is required"
            onChange={(e) => setBusinessLocation(e.target.value)}
          >
            <option key="nsw" value="New South Wales">New South Wales</option>
            <option key="vic" value="Victoria">Victoria</option>
            <option key="qld" value="Queensland">Queensland</option>
            <option key="sa" value="South Australia">South Australia</option>
            <option key="wa" value="Western Australia">Western Australia</option>
            <option key="tas" value="Tasmania">Tasmania</option>
            <option key="act" value="Australian Capital Territory">Australian Capital Territory</option>
            <option key="nt" value="Northern Territory">Northern Territory</option>
            <option key="other" value="Other">Other</option>
          </Select>

          {businessLocation === 'Other' && (
            <TextInput
              className="grid-businessLocationOther"
              label="Please Specify Your Location"
              maxLength={100}
              minLength={2}
              name="businessLocationOther"
              placeholder="Enter your location"
              required
              requiredErrorMessage="Location is required"
            />
          )}

          <Select
            className="grid-roleProgram"
            label="Which best describes your role in your companies travel program?"
            name="roleInTravelProgram"
            placeholder="Select your role"
            required
            requiredErrorMessage="Role in travel program is required"
          >
            <option key="traveller" value="Traveller">Traveller</option>
            <option key="travel-booker" value="Travel Booker">Travel Booker</option>
            <option key="program-manager" value="Program Manager">Program Manager</option>
            <option key="decision-maker" value="Decision Maker">Decision Maker</option>
          </Select>

          <Select
            className="grid-travelSize"
            label="How many people at your company travel for work?"
            name="companyTravelSize"
            placeholder="Select range"
            required
            requiredErrorMessage="Number of travelers is required"
          >
            <option key="1-9" value="1-9">1-9</option>
            <option key="10-24" value="10-24">10-24</option>
            <option key="25-49" value="25-49">25-49</option>
            <option key="50-99" value="50-99">50-99</option>
            <option key="100-200" value="100-200">100-200</option>
            <option key="200+" value="200+">200+</option>
          </Select>

          <Select
            className="grid-travelSolution"
            label="Current Travel Solution?"
            name="currentTravelSolution"
            placeholder="Select current solution"
            required
            requiredErrorMessage="Current travel solution is required"
          >
            <option key="travel-management-company" value="Travel Management Company">Travel Management Company</option>
            <option key="travel-manager" value="Travel Manager">Travel Manager</option>
            <option key="self-service" value="Self-Service">Self-Service (Internet)</option>
            <option key="other" value="Other">Other</option>
          </Select>

          <TextArea
            className="grid-message"
            label="Message"
            name="message"
            placeholder="Tell us more about your travel needs..."
            rows={4}
            maxLength={1000}
          />
        </InputGrid>
        <Group direction="horizontal" horizontalAlign="end">
          <TextButton disabled={isSubmitted} fit="mobile-full-width" kind="primary" size="large" type="submit">
            Submit
          </TextButton>
        </Group>
      </form>
    </Group>
  )
}
